import React from 'react';
import { GameState } from '../types/game';
import { Card } from './ui/Card';
import { XIcon, TrophyIcon, TrendingUpIcon, UsersIcon } from 'lucide-react';
interface StatsPanelProps {
  gameState: GameState;
  isOpen: boolean;
  onClose: () => void;
}
export function StatsPanel({
  gameState,
  isOpen,
  onClose
}: StatsPanelProps) {
  if (!isOpen) return null;
  const formatMoney = (val: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
      notation: 'compact'
    }).format(val);
  };
  // Calculate stats
  const peakHeadcount = Math.max(...gameState.history.map(h => h.headcount));
  const bestQuarterRevenue = Math.max(...gameState.history.map(h => h.revenue));
  const cumulativeProfit = gameState.history.reduce((acc, curr) => acc + curr.netIncome, 0);
  const totalRevenue = gameState.history.reduce((acc, curr) => acc + curr.revenue, 0);
  return <div className="fixed inset-0 z-50 flex justify-end" data-id="element-319">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-background/50 backdrop-blur-sm transition-opacity" onClick={onClose} data-id="element-320" />

      {/* Panel */}
      <div className="relative w-full max-w-md h-full bg-surface border-l border-border shadow-2xl animate-slide-left overflow-y-auto" data-id="element-321">
        <div className="p-6" data-id="element-322">
          <div className="flex justify-between items-center mb-8" data-id="element-323">
            <h2 className="text-xl font-bold text-text-primary flex items-center" data-id="element-324">
              <TrophyIcon className="w-5 h-5 text-primary mr-2" data-id="element-325" />
              Company Stats
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-surface-highlight rounded-full text-text-secondary hover:text-text-primary transition-colors" data-id="element-326">
              <XIcon className="w-5 h-5" data-id="element-327" />
            </button>
          </div>

          <div className="space-y-6" data-id="element-328">
            <Card className="bg-surface-highlight/30" data-id="element-329">
              <div className="text-xs text-text-secondary uppercase tracking-wider mb-2" data-id="element-330">
                Lifetime Revenue
              </div>
              <div className="text-3xl font-mono font-bold text-primary" data-id="element-331">
                {formatMoney(totalRevenue)}
              </div>
            </Card>

            <div className="grid grid-cols-2 gap-4" data-id="element-332">
              <Card className="bg-surface-highlight/30 p-4" data-id="element-333">
                <div className="flex items-center mb-2" data-id="element-334">
                  <TrendingUpIcon className="w-4 h-4 text-success mr-2" data-id="element-335" />
                  <span className="text-xs text-text-secondary uppercase" data-id="element-336">
                    Best Qtr Rev
                  </span>
                </div>
                <div className="text-xl font-mono font-bold text-text-primary" data-id="element-337">
                  {formatMoney(bestQuarterRevenue)}
                </div>
              </Card>

              <Card className="bg-surface-highlight/30 p-4" data-id="element-338">
                <div className="flex items-center mb-2" data-id="element-339">
                  <UsersIcon className="w-4 h-4 text-secondary mr-2" data-id="element-340" />
                  <span className="text-xs text-text-secondary uppercase" data-id="element-341">
                    Peak Team
                  </span>
                </div>
                <div className="text-xl font-mono font-bold text-text-primary" data-id="element-342">
                  {peakHeadcount}
                </div>
              </Card>
            </div>

            <div className="border-t border-border pt-6" data-id="element-343">
              <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-4" data-id="element-344">
                Performance History
              </h3>
              <div className="space-y-3" data-id="element-345">
                {gameState.history.slice().reverse().slice(0, 8).map(h => <div key={h.turn} className="flex justify-between items-center text-sm p-2 hover:bg-surface-highlight rounded transition-colors" data-id="element-346">
                      <span className="font-mono text-text-secondary" data-id="element-347">
                        Q{h.turn}
                      </span>
                      <div className="flex space-x-6" data-id="element-348">
                        <span className="text-text-primary" data-id="element-349">
                          Rev: {formatMoney(h.revenue)}
                        </span>
                        <span className={`font-mono w-20 text-right ${h.netIncome >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-350">
                          {h.netIncome > 0 ? '+' : ''}
                          {formatMoney(h.netIncome)}
                        </span>
                      </div>
                    </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}